import { FC, useState } from 'react';
import { useStyles } from './styles';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useStore } from 'hooks/useRootStore';
import { useHistory } from 'react-router-dom';
import { useStripe } from '@stripe/react-stripe-js';
import { StripeACHResponse } from 'containers/UpgradeLicense';
import { ACHFormFields } from './types';
import { Grid, Paper, Button, TextField, FormControl, NativeSelect } from '@material-ui/core';
import { formatAmount } from 'utils';

export const ACHForm: FC = () => {
  const classes = useStyles();
  const history = useHistory();

  const {
    upgradeLicenseStore: { invoice, setStep, postStripeACHToken },
    notificationStore: { openNotification },
  } = useStore();

  const [processing, setProcessing] = useState<boolean>(false);

  const stripe = useStripe();

  const { mutate, isLoading } = useMutation((data: StripeACHResponse) => postStripeACHToken(data), {
    mutationKey: 'createAdminReq',
    onSuccess: () => {
      openNotification('success', `ACH request sent`);
      setStep('upgrade-form');
      history.goBack();
    },
    onError: ({ message }: Error) => openNotification('error', message),
  });

  const { control, handleSubmit } = useForm<ACHFormFields>();

  const onSubmit = ({ routing_number, account_number, account_holder_name, account_holder_type }: ACHFormFields) => {
    stripe
      ?.createToken('bank_account', {
        country: 'US',
        currency: 'usd',
        routing_number,
        account_number,
        account_holder_name,
        account_holder_type,
      })
      .then((response) => {
        if (response?.error) {
          throw new Error(response?.error.message);
        }
        mutate(response?.token as unknown as StripeACHResponse);
        setProcessing(false);
      })
      .catch((error) => {
        setProcessing(false);
        openNotification('error', `Payment failed ${error.message}`);
      });
  };

  useAppPreloader(processing || isLoading);

  return (
    <Grid container justify='center' alignItems='center'>
      <Grid item xs={6} style={{ maxWidth: 420 }}>
        <Paper className={classes.paper}>
          <Grid container justify='space-between'>
            <p className={classes.title}>Payment Details</p>
            <p className={classes.licenseInfoTitle}>{`License price: ${formatAmount(invoice?.originalAmount)}`}</p>
            <p className={classes.licenseInfoTitle}>{`Remaining from the previous license: ${formatAmount(
              invoice?.amountFromPreviousPayment
            )}`}</p>
            <p className={classes.licenseInfoTitle}>{`Recalculated amount: ${formatAmount(invoice?.amount)}`}</p>
          </Grid>
          <form className={classes.formWraper} onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name='account_number'
              defaultValue=''
              control={control}
              rules={{ required: true, pattern: /^[0-9]+$/ }}
              render={({ field, fieldState: { invalid, error } }) => (
                <TextField
                  {...field}
                  className={classes.textField}
                  label='Bank Routing #'
                  autoFocus
                  helperText={
                    invalid ? (error?.type === 'pattern' ? 'This field must be a number' : 'Required field') : ''
                  }
                  error={invalid}
                />
              )}
            />
            <Controller
              name='routing_number'
              defaultValue=''
              control={control}
              rules={{ required: true, pattern: /^[0-9]+$/ }}
              render={({ field, fieldState: { invalid, error } }) => (
                <TextField
                  {...field}
                  className={classes.textField}
                  label='Bank Account #'
                  autoFocus
                  helperText={
                    invalid ? (error?.type === 'pattern' ? 'This field must be a number' : 'Required field') : ''
                  }
                  error={invalid}
                />
              )}
            />
            <Controller
              name='account_holder_name'
              defaultValue=''
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { invalid } }) => (
                <TextField
                  {...field}
                  className={classes.textField}
                  label='Beneficiary Name'
                  helperText={invalid ? 'Required field' : ''}
                  error={invalid}
                />
              )}
            />
            <Controller
              name='account_holder_type'
              defaultValue='individual'
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { invalid } }) => (
                <FormControl className={classes.select}>
                  {/* <p className={classes.fieldDesc}>Choose payment method:</p> */}
                  <NativeSelect {...field}>
                    <option value='individual'>Individual</option>
                    <option value='company'>Business</option>
                  </NativeSelect>
                </FormControl>
              )}
            />
            <div className={classes.btnBox}>
              <Button className={classes.button} onClick={() => setStep('upgrade-form')} disabled={processing}>
                Back
              </Button>
              <Button
                className={classes.button}
                disabled={processing}
                type='submit'
                variant='contained'
                color='primary'>
                Submit
              </Button>
            </div>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
};
